import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, ChevronDown, ChevronUp, Edit2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { MinimalEditor } from "./reactflow/infoPopover/MinimalEditor";
import { RecordingPlayer } from "./recording/RecordingPlayer";
import { ImageViewer } from "./images/ImageViewer";
import { StoryEndSlideCard } from "./StoryEndSlideCard";

// Helper to check if HTML content is empty
const hasHtmlContent = (html) => {
  if (!html) return false;
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length > 0;
};

// Helper to check if recordings or image exist
const hasRecordings = (recordings) => {
  return recordings?.audio || recordings?.video || recordings?.image;
};

const RecordingsBlock = ({ recordings }) => {
  if (!hasRecordings(recordings)) return null;
  return (
    <div className="mt-2 space-y-1">
      {recordings.audio && (
        <RecordingPlayer
          recording={recordings.audio}
          type="audio"
          compact
          readOnly
        />
      )}
      {recordings.video && (
        <RecordingPlayer
          recording={recordings.video}
          type="video"
          compact
          readOnly
        />
      )}
      {recordings.image && (
        <ImageViewer image={recordings.image} compact readOnly />
      )}
    </div>
  );
};

/**
 * Banner component for displaying prologue in review/example mode
 * Shows at the top of the engagement, before the first pane
 * Expandable/collapsible to save space (when not used as a slideshow pane)
 * Can be editable when in edit mode
 */
const PrologueBanner = ({
  prologue,
  isEditMode = false,
  onChange,
  recordings,
  alwaysShowAsPane = false,
  subtitle = undefined,
}) => {
  const { t } = useTranslation();
  const isEditable = onChange !== undefined;
  const [isExpanded, setIsExpanded] = useState(
    isEditable || alwaysShowAsPane ? true : false
  );

  // Don't render if no prologue content, no recordings, and not editable (unless used as a dedicated pane)
  if (
    !alwaysShowAsPane &&
    !hasHtmlContent(prologue) &&
    !hasRecordings(recordings) &&
    !isEditable
  ) {
    return null;
  }

  const titleSlot = (
    <span className="inline-flex items-center justify-center gap-1.5 flex-wrap">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help underline decoration-dotted decoration-primary/70">
              {t("prologue")}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {t("tooltip_prologue")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {isEditable && <Edit2 className="w-4 h-4 shrink-0 opacity-80" />}
    </span>
  );

  const slideBody = isEditable ? (
    <div className="mx-5">
      <MinimalEditor
        value={prologue || ""}
        onChange={onChange}
        placeholder={
          t("prologue_placeholder") ||
          "What situation led you to this engagement?"
        }
      />
      <RecordingsBlock recordings={recordings} />
    </div>
  ) : (
    <>
      {hasHtmlContent(prologue) ? (
        <div
          className="prose prose-sm max-w-none text-foreground break-words mx-5"
          dangerouslySetInnerHTML={{ __html: prologue }}
        />
      ) : alwaysShowAsPane ? (
        <p className="text-sm text-muted-foreground italic mx-5">
          {t("prologue_placeholder") ||
            "What situation led you to this engagement?"}
        </p>
      ) : null}
      <div className="mx-5">
        <RecordingsBlock recordings={recordings} />
      </div>
    </>
  );

  if (alwaysShowAsPane) {
    return (
      <StoryEndSlideCard
        variant="prologue"
        icon={BookOpen}
        titleSlot={titleSlot}
        subtitle={subtitle}
      >
        {slideBody}
      </StoryEndSlideCard>
    );
  }

  return (
    <div className="bg-primary/5/80 border-l-2 border-primary/40 mx-4 mt-2 mb-1.5 rounded-r-md shadow-sm">
      <div
        className={`flex items-start gap-2 py-2 px-3 ${
          !isEditable ? "cursor-pointer hover:bg-primary/10/50" : ""
        } transition-colors`}
        onClick={!isEditable ? () => setIsExpanded(!isExpanded) : undefined}
      >
        <BookOpen className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-0.5">
            <div>
              <h4 className="text-xs font-semibold text-primary flex items-center gap-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help underline decoration-dotted decoration-blue-600">
                        {t("prologue")}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      {t("tooltip_prologue")}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {isEditable && <Edit2 className="w-3 h-3" />}
              </h4>
              {subtitle && (
                <p className="text-xs text-primary/80 mt-0.5">{subtitle}</p>
              )}
            </div>
            {!isEditable &&
              (isExpanded ? (
                <ChevronUp className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-primary flex-shrink-0" />
              ))}
          </div>
          {isEditable ? (
            <div className="mt-1">
              <MinimalEditor
                value={prologue || ""}
                onChange={onChange}
                placeholder={
                  t("prologue_placeholder") ||
                  "What situation led you to this engagement?"
                }
              />
              <RecordingsBlock recordings={recordings} />
            </div>
          ) : (
            isExpanded && (
              <>
                {hasHtmlContent(prologue) ? (
                  <div
                    className="prose prose-sm max-w-none text-sm text-gray-700 break-words mt-1"
                    dangerouslySetInnerHTML={{ __html: prologue }}
                  />
                ) : null}
                <RecordingsBlock recordings={recordings} />
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default PrologueBanner;
